import { A } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { For, Show } from "solid-js";
import { isAdmin } from "../../store/admin";
import { StatusActions } from "../issues/StatusActions";
import type { WebsiteContext } from "../website/store";
import { Metric } from "./issueDetailParts";
import { uniqueCurrentPagesForIssue } from "./issuePageResolution";
import {
  clientIssueTitle,
  issueDimensionLabel,
  issueEvidenceStateForSite,
  rationaleForIssueInSite,
  siteSpecificRecommendationsForIssue,
  suggestedActionForIssueInSite,
} from "./issueText";

export function IssueDetailPanels(props: {
  data: OverviewResponse;
  issue: Issue;
  context: WebsiteContext;
  briefEligibility: { eligible: boolean; reason: string | null };
  briefCreated: boolean;
  briefHref: string;
  onEdit: () => void;
}) {
  const primaryRecommendation = () =>
    suggestedActionForIssueInSite(props.data, props.issue);
  const recommendations = () =>
    siteSpecificRecommendationsForIssue(props.data, props.issue);
  const additionalRecommendations = () =>
    recommendations().filter((item) => item !== primaryRecommendation());
  const affectedPageCount = () =>
    uniqueCurrentPagesForIssue(props.data, props.issue).length;

  return (
    <>
      <div class="scan-detail-metrics scan-issue-facts">
        <Metric label="Dimension" value={issueDimensionLabel(props.issue)} />
        <Metric
          label={affectedPageCount() === 1 ? "Affected page" : "Affected pages"}
          value={`${affectedPageCount()}`}
        />
        <Metric label="Owner" value={props.issue.ownerName ?? "Unassigned"} />
        <Metric
          label="Evidence"
          value={issueEvidenceStateForSite(props.data, props.issue)}
        />
      </div>
      <div class="scan-grid-two scan-detail-grid">
        <section class="scan-panel scan-issue-summary">
          <span class="scan-kicker">Issue summary</span>
          <p class="scan-lede">
            {rationaleForIssueInSite(props.data, props.issue)}
          </p>

          <Show when={isAdmin()}>
            <StatusActions
              issue={props.issue}
              apply={(status) =>
                props.context.setIssueStatus(props.issue, status)
              }
            />
            <p class="scan-muted">
              Verified fixed is set after a later complete scan no longer
              reproduces the issue.
            </p>
          </Show>
          <Show when={isAdmin()}>
            <button class="btn" type="button" onClick={() => props.onEdit()}>
              Edit issue
            </button>
          </Show>
        </section>

        <section class="scan-panel scan-issue-action">
          <span class="scan-kicker">Recommended action</span>
          <p class="scan-lede">{primaryRecommendation()}</p>
          <Show
            when={recommendations().length > 0}
            fallback={
              <p class="scan-muted">
                No site-specific recommendation is available for this finding
                yet.
              </p>
            }
          >
            <Show when={additionalRecommendations().length > 0}>
              <details class="scan-disclosure scan-additional-recommendations">
                <summary>
                  Additional recommendations (
                  {additionalRecommendations().length})
                </summary>
                <ul class="scan-recommendation-list">
                  <For each={additionalRecommendations()}>
                    {(item) => <li>{item}</li>}
                  </For>
                </ul>
              </details>
            </Show>
          </Show>
          <Show
            when={props.briefEligibility.eligible}
            fallback={<p class="scan-muted">{props.briefEligibility.reason}</p>}
          >
            <p class="scan-muted" id="issue-brief-description">
              Draft the work needed to fix this finding from its recorded scope
              and evidence. Preview changes are not saved.
            </p>
            <div class="signal-inline-actions">
              <A
                class="scan-btn scan-btn-primary"
                href={props.briefHref}
                aria-label={`${props.briefCreated ? "Open" : "Create"} draft Brief for ${clientIssueTitle(props.issue)}`}
                aria-describedby="issue-brief-description"
              >
                {props.briefCreated ? "Open draft Brief" : "Create draft Brief"}
              </A>
            </div>
          </Show>
        </section>
      </div>
    </>
  );
}
